<template>
    <div class="w-full flex justify-between">
        <div class="w-1/4 border-primary-20 rounded-4 py-8 px-8 border-2 mt-80 mb-[7rem]">
            <div :class="[hasDatesInThePast ? 'max-h-360 overflow-hidden' : '']">
                <div class="flex flex-col items-center justify-center mb-12">
                    <p class="text-black font-semibold">{{ $t('interview.timeslots.suggested_timeslots') }}:</p>
                </div>
                <div
                    class="flex flex-col space-y-8 overflow-y-auto hide-scrollbar"
                    :class="[hasDatesInThePast ? 'max-h-360' : 'max-h-550']"
                >
                    <div
                        v-for="slot in validTimeslots"
                        class="flex justify-between rounded-6 py-5 px-12"
                        :class="slot.notAvailableForAll ? 'bg-semantic-warning' : 'bg-primary'"
                    >
                        <div class="flex">
                            <div v-if="slot.notAvailableForAll" class="flex justify-center items-center mr-12">
                                <svg-icon type="warning" class="text-white max-w-20 max-h-20"></svg-icon>
                            </div>
                            <p class="text-white font-semibold text-body-sm" v-html="formattedTimeslot(slot)"></p>
                        </div>
                        <div
                            @click="removeCalendarTimeslot(slot)"
                            class="flex cursor-pointer justify-center items-center"
                        >
                            <svg-icon type="cancel" class="text-white max-w-14 max-h-14"></svg-icon>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="hasDatesInThePast">
                <div class="flex flex-col items-center justify-center mt-8 mb-12">
                    <p>{{ $t('interview.timeslots.suggested_timeslots_in_past') }}:</p>
                </div>
                <div
                    class="flex flex-col space-y-8 max-h-550 overflow-y-auto hide-scrollbar"
                >
                    <div
                        v-for="slot in invalidTimeslots"
                        class="flex justify-between bg-primary-5 rounded-6 py-5 px-12"
                    >
                        <div class="flex">
                            <div class="flex justify-center items-center mr-12">
                                <svg-icon type="warning" class="text-semantic-warning max-w-20 max-h-20"></svg-icon>
                            </div>
                            <p class="text-primary-80 font-semibold text-body-sm" v-html="formattedTimeslot(slot)"></p>
                        </div>
                        <div @click="removeCalendarTimeslot(slot)" class="flex cursor-pointer justify-center items-center">
                            <svg-icon type="cancel" class="text-primary-80 max-w-14 max-h-14"></svg-icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="w-3/4 ml-24">
            <FullCalendar
                ref="fullCalendar"
                :options='calendarOptions'
            />

            <div class="flex justify-between my-16 ml-48 mr-8">
                <div class="flex">
                    <div class="flex items-center mr-24">
                        <div class="w-16 h-16 bg-primary-40 border-primary-20 border rounded-6 mr-8"></div>
                        <span>{{ $t('calendar.availability.available') }}</span>
                    </div>
                    <div class="flex items-center">
                        <div class="w-16 h-16 bg-white border-primary-20 border rounded-6 mr-8"></div>
                        <span>{{ $t('calendar.availability.non-available') }}</span>
                    </div>
                </div>
                <toggle v-model="form.sync">{{ $t('calendar.auto_sync') }}</toggle>
            </div>
        </div>


        <popup-modal-confirm
            v-if="multipleTimeslotWarning"
            :title="$t('general.are_you_sure')"
            :message="$t('interview.timeslots.multiple_timeslot_warning')"
            confirm-button="success"
            show-exclamation
            @close="undoSlotConfirmed"
            @confirm="removeTimeslots"
        ></popup-modal-confirm>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import allLocales from '@fullcalendar/core/locales-all';
import interactionPlugin from '@fullcalendar/interaction';
import moment from "moment";
import Loading from "./ui/Loading.vue";
import Form from "../etc/forms/Form";
import PopupModalConfirm from "./ui/PopupModalConfirm.vue";
import {mapState} from "pinia";
import {useMainStore} from '../state/store';
import SvgIcon from "./ui/SvgIcon.vue";
import Toggle from "./forms/Toggle.vue";
import styles from "../../../../../styles.config";

export default {
    components: {
        Toggle,
        SvgIcon,
        PopupModalConfirm,
        Loading,
        FullCalendar
    },
    props: {
        form: Form,
        availablePeriods: {type: Array},
        creating: {type: Boolean, default: false},
    },
    data: function() {
        return {
            multipleTimeslotWarning: false,
            validTimeslot: false,
            bgEventsLoaded: false,
            fullCalendar: null,
            events: [],
            timeslots: [],
            minutesToAdd: 0,
            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView: 'timeGridWeek',
                dayHeaderContent: (value) => {
                    if (value.text.includes('-')) {
                        let string = value.text.replace('-', '/');
                        return `${string.charAt(0).toUpperCase() + string.slice(1)}`;
                    } else {
                        return value.text;
                    }
                },
                slotLabelFormat: {
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                    omitZeroMinute: false,
                },
                slotLabelDidMount: (value) => {
                    // value.el.parentNode.style.position = 'relative';
                    // value.el.style.position = 'absolute';
                    // value.el.style.top = '-60px';
                },
                now: () => {
                    return null;
                },
                scrollTime: '08:00:00',
                selectable: false,
                selectMirror: false,
                eventStartEditable: false,
                editable: false,
                slotLabelInterval: {hours: 1},
                allDaySlot: false,
                locales: allLocales,
                locale: 'en', // the initial locale
                height: 700,
                contentHeight: 60,
                forceEventDuration: false,
                defaultTimedEventDuration: '00:05',
                selectMinDistance: 1,
                eventOverlap: false,
                weekends: true,
                events: [],
                eventClick: (event) => {
                    if (this.form.calendar_open) {
                        return;
                    }

                    if (event.event.groupId === 'available-timeslot' &&
                        event.el.clientHeight >= this.form.duration &&
                        !(this.form.slot_confirmed && this.form.time_slots.length >= 1)
                    ) {
                        this.createCalendarEvent(event.event)
                    } else if (event.event.groupId === 'timeslot' || event.event.groupId === 'tmpEvent') {
                        this.removeEvent(event, event.event.groupId === 'tmpEvent')
                    }
                },
                eventMouseEnter: (event) => {
                    // add event hover color
                    if (event.event.groupId === 'timeslot' || event.event.groupId === 'tmpEvent') {
                        if (event.event.extendedProps.isPast) {
                            event.el.style.backgroundColor = this.eventHoverInPastColor;
                        } else if (event.event.extendedProps.notAvailableForAll) {
                            event.el.style.backgroundColor = this.eventHoverNotAvailableForAllColor;
                        } else {
                            event.el.style.backgroundColor = this.eventHoverColor;
                            event.el.style.borderColor = this.eventHoverColor;
                        }
                    }

                    if (!this.form.calendar_open &&
                        event.event.groupId === 'available-timeslot' &&
                        event.el.clientHeight >= this.form.duration &&
                        !(this.form.slot_confirmed && this.form.time_slots.length >= 1)
                    ) {
                        this.createHover(event);
                    }
                },
                eventMouseLeave: (event) => {
                    // remove event hover color
                    if (event.event.groupId === 'timeslot' || event.event.groupId === 'tmpEvent') {
                        if (event.event.extendedProps.isPast) {
                            event.el.style.backgroundColor = this.eventInPastColor;
                        } else if (event.event.extendedProps.notAvailableForAll) {
                            event.el.style.backgroundColor = this.eventNotAvailableForAllColor;
                        } else {
                            event.el.style.backgroundColor = this.eventColor;
                            event.el.style.borderColor = this.eventBorderColor;
                        }
                    }

                    document.getElementById('hover-timeslot')?.remove();
                },
            }
        }
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale']),
        styles() {
            return styles;
        },
        eventColor() {
            return styles.colors.blue.DEFAULT;
        },
        eventInPastColor() {
            return styles.colors.grey.lightest;
        },
        eventNotAvailableForAllColor() {
            return styles.colors.orange.DEFAULT;
        },
        eventTextColor() {
            return styles.colors.white;
        },
        eventTextInPastColor() {
            return styles.colors.grey.dark;
        },
        eventBorderColor() {
            return styles.colors.blue.DEFAULT;
        },
        eventBorderInPastColor() {
            return styles.colors.orange.DEFAULT;
        },
        eventBorderNotAvailableForAllColor() {
            return styles.colors.orange.DEFAULT;
        },
        eventHoverColor() {
            return styles.colors.blue.dark;
        },
        eventHoverInPastColor() {
            return styles.colors.grey.lighter;
        },
        eventHoverNotAvailableForAllColor() {
            return styles.colors.orange.light;
        },
        availablePeriodColor() {
            return styles.colors.blue.light;
        },
        nonAvailablePeriodColor() {
            return this.styles.colors.white;
        },
        calendarTimeslots() {
            return this.events.filter(event => event.groupId === 'timeslot' || event.groupId === 'tmpEvent')
                .sort((a, b) => {
                    return moment(a.start).isBefore(moment(b.start)) ? -1 : 1;
                });
        },
        validTimeslots() {
            return this.calendarTimeslots.filter(event => !event.isPast);
        },
        invalidTimeslots() {
            return this.calendarTimeslots.filter(event => event.isPast);
        },
        checkForWeekend() {
            return this.availablePeriods.filter(period => {
                return moment(period.start).day() === 6 || moment(period.start).day() === 0;
            }).length > 0;
        },
        hasDatesInThePast() {
            return this.calendarTimeslots.filter(event => event.isPast).length;
        },
    },
    methods: {
        formattedTimeslot(slot) {
            let start = moment(slot.start).locale(this.selectedLocale);
            return `${start.format('ddd').charAt(0).toUpperCase() + start.format('ddd').slice(1)}
                ${start.format('D')} ${start.format('MMM')} <br>
                ${start.format('HH:mm')} - ${moment(slot.end).format('HH:mm')}`
        },
        removeCalendarTimeslot(slot) {
            this.removeEvent({event: slot}, slot.groupId === 'tmpEvent');
        },
        dateIsInPast(date) {
            return moment(date).isBefore(moment());
        },
        findAvailablePeriodByDateAndRemove(startTime, endTime) {
            startTime = moment(startTime);
            endTime = moment(endTime);
            let periodIndex = this.events.findIndex(event => {
                const startDate = moment(event.start);
                const endDate = moment(event.end);
                    return event.groupId === 'available-timeslot' &&
                    ((startTime.isBetween(startDate, endDate) || endTime.isBetween(startDate, endDate)) ||
                        (startTime.isSame(startDate) || endTime.isSame(startDate)) ||
                        (startTime.isSame(endDate) || endTime.isSame(endDate)));
            }),
                period = this.events[periodIndex];

            if (periodIndex >= 0) {
                // remove event since we create new period
                this.events.splice(periodIndex, 1);
            }

            return period;
        },
        createHover(event) {
            let parent = event.jsEvent.target.parentNode,
                duration = this.form.duration;

            parent.style.zIndex = 99;

            let hover = document.createElement('div');
            let text = document.createElement('p');
            hover.id = 'hover-timeslot'
            hover.style.position = 'absolute';
            hover.style.top = 0;
            hover.style.height = `${duration}px`;
            hover.style.width = `100%`;
            hover.style.backgroundColor = this.eventHoverColor;
            hover.style.zIndex = 99;
            hover.classList.add('rounded-6', 'pointer-events-none');
            // hover.style.borderWidth = '1px';
            // hover.style.borderColor = 'transparent';

            text.style.color = styles.colors.white;
            text.style.width = '100%';
            text.style.fontWeight = 'bold';
            text.style.fontSize = "1rem";
            text.style.padding = '2px';
            text.style.display = 'flex';
            text.style.justifyContent = 'center';
            text.style.alignItems = 'center';

            text.style.position = 'absolute';
            text.style.top = '50%';
            text.style.left = '50%';
            text.style.transform = 'translate(-50%, -50%)';

            this.setHoverPosition(parent, hover, text, event, event.jsEvent.clientY)

            parent.addEventListener('mousemove', (e) => {
                this.setHoverPosition(parent, hover, text, event, e.clientY)
            })

            hover.appendChild(text)
            parent.appendChild(hover, parent.firstChild);
        },
        setHoverPosition(parent, hover, text, event, clientY) {
            const duration = parseInt(this.form.duration);
            const height = parseInt(parent.clientHeight);
            const rect = parent.getBoundingClientRect();
            const y = clientY - rect.top;
            let start = moment(event.event.start);
            let newTop = Math.round((y - (duration / 2)) / 5) * 5;

            // overwrite newTop if out of it's boundaries
            if (newTop <= 0) newTop = 0;
            else if ((newTop + duration) > height) newTop = Math.round((height - duration) / 5) * 5;

            this.minutesToAdd = newTop;
            hover.style.top = `${newTop}px`;
            if (duration >= 20) {
                text.innerText = `${start.add(newTop, 'minutes').format('HH:mm')} - ${start.add(duration, 'minutes').format('HH:mm')}`
            }
        },
        setEvents() {
            this.calendarOptions.events = this.events;
            this.fullCalendarApi.refetchEvents();
        },
        splitAvailabilityPeriod(timeslot, period = null) {
            if (!period) {
                period = this.findAvailablePeriodByDateAndRemove(timeslot.start, timeslot.end);
            }

            if (period && !moment(period.start).isSame(moment(timeslot.start))) {
                this.events.push({
                    overlap: false,
                    groupId: `available-timeslot`,
                    start: period.start,
                    end: timeslot.start,
                    display: 'background',
                    backgroundColor: this.availablePeriodColor,
                });
            }

            if (period && moment(timeslot.end).isAfter(moment(period.end))) return;

            if (period && !moment(timeslot.end).isSame(moment(period.end))) {
                this.events.push({
                    overlap: false,
                    groupId: `available-timeslot`,
                    start: timeslot.end,
                    end: period.end,
                    display: 'background',
                    backgroundColor: this.availablePeriodColor,
                });
            }
        },
        createCalendarEvent(event) {
            let startTime = moment(event.start).add(this.minutesToAdd, 'minutes'),
                timeslot = {
                    groupId: 'tmpEvent',
                    overlap: false,
                    start: startTime.toISOString(),
                    end: startTime.add(this.form.duration, 'minutes').toISOString(),
                    backgroundColor: this.eventColor,
                    borderColor: this.eventBorderColor,
                };

            this.splitAvailabilityPeriod(timeslot)

            this.events.push(timeslot);
            this.setEvents();
        },
        removeEvent(rawEvent, isTmpEvent = false) {
            let event = rawEvent.event;
            let startDate = moment(event.start);
            let endDate = moment(event.end);
            let firstPeriod = this.findAvailablePeriodByDateAndRemove(startDate);
            let secondPeriod = this.findAvailablePeriodByDateAndRemove(endDate);

            if (firstPeriod && !secondPeriod) {
                this.events.push({
                    overlap: false,
                    groupId: `available-timeslot`,
                    start: firstPeriod.start,
                    end: event.end,
                    display: 'background',
                    backgroundColor: this.availablePeriodColor,
                });
            } else if (!firstPeriod && secondPeriod) {
                this.events.push({
                    overlap: false,
                    groupId: `available-timeslot`,
                    start: event.start,
                    end: secondPeriod.end,
                    display: 'background',
                    backgroundColor: this.availablePeriodColor,
                });
            } else if (firstPeriod && secondPeriod) {
                this.events.push({
                    overlap: false,
                    groupId: `available-timeslot`,
                    start: firstPeriod.start,
                    end: secondPeriod.end,
                    display: 'background',
                    backgroundColor: this.availablePeriodColor,
                });
            } else {
                // check if event is in available period
                let period = this.availablePeriods.find(period => {
                    return moment(period.start).isSame(startDate) && moment(period.end).isSame(endDate);
                });
                if (period) {
                    this.events.push({
                        overlap: false,
                        groupId: `available-timeslot`,
                        start: event.start,
                        end: event.end,
                        display: 'background',
                        backgroundColor: this.availablePeriodColor,
                    });
                }
            }

            // remove event from calendar
            let index = this.events.findIndex(e => (e.groupId === 'tmpEvent' || e.groupId === 'timeslot') && (moment(e.start).isSame(event.start) || moment(e.end).isSame(event.end)));
            if (index >= 0) {
                this.events.splice(index, 1);
            }

            this.setEvents();
        },
        removeBgEvents() {
            this.events = this.events.filter(event => event.groupId !== 'available-timeslot' && event.groupId !== 'non-available-timeslot');
            this.setEvents();
        },
        prepareBgEvents() {
            this.events = this.events.filter(event => event.groupId !== 'available-timeslot' && event.groupId !== 'non-available-timeslot');
            this.availablePeriods.forEach(period => {
                this.events.push({
                    overlap: false,
                    groupId: `available-timeslot`,
                    start: period.start,
                    end: period.end,
                    display: 'background',
                    backgroundColor: this.availablePeriodColor,
                })
                this.events.push({
                    overlap: false,
                    groupId: `non-available-timeslot`,
                    start: period.start,
                    end: period.end,
                    display: 'inverse-background',
                    backgroundColor: this.nonAvailablePeriodColor,
                })
            })
            this.setEvents();
            this.bgEventsLoaded = true;
        },
        minutesToHoursAndMinutes(totalMinutes) {
            let hours = ('0' + Math.floor(totalMinutes / 60)).slice(-2),
                minutes = ('0' + totalMinutes % 60).slice(-2);
            return `${hours}:${minutes}`;
        },
        undoSlotConfirmed() {
            this.form.slot_confirmed = false;
            this.multipleTimeslotWarning = false;
        },
        removeTimeslots() {
            // reset calendar events
            this.events = [];
            this.prepareBgEvents();

            this.events = this.events.filter(event => event.groupId !== 'tmpEvent' || event.groupId === 'timeslot');
            this.multipleTimeslotWarning = false;
        },
        reformatBgEvents() {
            let events = this.events.filter(event => event.groupId === 'tmpEvent' || event.groupId === 'timeslot');
            events.forEach(event => this.splitAvailabilityPeriod(event));
        },
        addTimeslots() {
            let events = this.form.time_slots.map(timeslot => {
                const eventIsInPast = this.dateIsInPast(timeslot.date);
                const eventIsNotAvailableForAll = this.timeslotIsNotAvailableForAll(timeslot);
                let event = {
                    groupId: timeslot.id ? 'timeslot' : 'tmpEvent',
                    overlap: false,
                    start: moment(timeslot.date).toISOString(),
                    end: moment(timeslot.date).add(this.form.duration, 'minutes').toISOString(),
                    backgroundColor: eventIsInPast ? this.eventInPastColor : eventIsNotAvailableForAll
                        ? this.eventNotAvailableForAllColor : this.eventColor,
                    textColor: eventIsInPast ? this.eventTextInPastColor : this.eventTextColor,
                    borderColor: eventIsInPast ? this.eventBorderInPastColor : eventIsNotAvailableForAll
                        ? this.eventBorderNotAvailableForAllColor : this.eventBorderColor,
                    isPast: eventIsInPast,
                    notAvailableForAll: eventIsNotAvailableForAll
                }

                // calendar id must be unique, undefined/null is allowed but not unique
                if (timeslot.id) event.id = timeslot.id;

                return event;
            });
            this.events = [
                ...this.events,
                ...events
            ];
            this.setEvents();
        },
        removeTimeslotsInPast() {
            let eventsInPast = this.events.filter(event => event.isPast);
            eventsInPast.forEach(event => this.removeCalendarTimeslot(event))
        },
        setDurationOnEvents() {
            let events = this.events.filter(event => event.groupId === 'tmpEvent' || event.groupId === 'timeslot');
            events.forEach(event => {
                event.end = moment(event.start).add(this.form.duration, 'minutes').toISOString()
            });

            this.removeBgEvents();
            this.prepareBgEvents();
            this.reformatBgEvents();
            this.setEvents();
        },
        timeslotIsNotAvailableForAll(slot, field = 'date') {
            return !this.availablePeriods.find(availableSlot => {
                return moment(slot[field]).isBetween(moment(availableSlot.start), moment(availableSlot.end), null, '[]') &&
                    moment(slot[field]).add(this.duration, 'minutes').isBetween(moment(availableSlot.start), moment(availableSlot.end), null, '[]');
            })
        },
        checkEventStatus() {
            this.calendarTimeslots.forEach(event => {
                const eventIsInPast = this.dateIsInPast(event.start);
                const eventIsNotAvailableForAll = this.timeslotIsNotAvailableForAll(event, 'start');
                event.backgroundColor = eventIsInPast ? this.eventInPastColor : eventIsNotAvailableForAll
                    ? this.eventNotAvailableForAllColor : this.eventColor;
                event.textColor = eventIsInPast ? this.eventTextInPastColor : this.eventTextColor;
                event.borderColor = eventIsInPast ? this.eventBorderInPastColor : eventIsNotAvailableForAll
                    ? this.eventBorderNotAvailableForAllColor : this.eventBorderColor;
                event.isPast = eventIsInPast;
                event.notAvailableForAll = eventIsNotAvailableForAll;
            })
            this.setEvents();
        },
    },
    watch: {
        'bgEventsLoaded'() {
            if (this.bgEventsLoaded) {
                this.setEvents();
            }
        },
        'events'() {
            this.form.time_slots = this.events
                .filter(event => event.groupId === 'timeslot' || event.groupId === 'tmpEvent')
                .map(event => {
                    return {id: event.id ?? null, date: event.start, available: true}
                });
        },
        'form.duration'() {
            // this.calendarOptions.defaultTimedEventDuration =  this.minutesToHoursAndMinutes(this.form.duration);
            this.setDurationOnEvents();
        },
        'form.slot_confirmed'() {
            if (this.form.slot_confirmed && this.form.time_slots.length > 1) {
                this.multipleTimeslotWarning = true;
            }
        },
        'form.calendar_open'() {
            if (this.form.calendar_open) {
                this.removeTimeslots();
            }
        },
        'availablePeriods': {
            deep: true,
            handler() {
                this.calendarOptions.weekends = this.checkForWeekend;
                this.checkEventStatus();
                this.prepareBgEvents();
                this.reformatBgEvents();
            }
        }
    },
    created() {
        this.calendarOptions.weekends = this.checkForWeekend;
    },
    mounted() {
        this.fullCalendar = this.$refs.fullCalendar;
        this.fullCalendarApi = this.fullCalendar.getApi();
        this.fullCalendarApi.setOption('locale', this.selectedLocale);
        this.$nextTick(() => {
            if (!this.creating) {
                this.addTimeslots();
            }
            this.prepareBgEvents();
            this.reformatBgEvents();
            this.fullCalendarApi.refetchEvents();
        })
    }
}
</script>

<style>
.fc-theme-standard td, .fc-theme-standard th {
    border-width: 2px;
}

.fc .fc-scrollgrid,
.fc .fc-timegrid-axis,
.fc .fc-scrollgrid-shrink,
.fc .fc-timegrid-slot-label,
.fc .fc-scrollgrid-section-header th:first-child,
.fc .fc-scrollgrid-section-body td:first-child,
.fc .fc-col-header-cell {
    border: none;
}

.fc .fc-col-header-cell {
    border-bottom: 2px solid var(--fc-border-color);
}

.fc .fc-timegrid-slot-minor {
    border-top-style: dashed;
}

.fc .fc-scrollgrid-section-header .fc-scroller {
    overflow: hidden !important;
}

.fc .fc-timegrid-col {
    border-top: 1px;
    border-bottom: 1px;
}

.fc .fc-toolbar-title {
    font-size: 2.2rem;
    font-weight: bold;
}

.fc .fc-timegrid-slot {
    height: 30px;
}

.fc .fc-timegrid-col-events {
    margin: 0
}

.fc .fc-v-event {
    border-width: 2px;
    margin: 1px 0;
    box-shadow: none;
}

.fc .fc-event-main-frame {
    position: relative;
}

.fc .fc-event-time {
    position: absolute;
    font-size: 1rem;
    font-weight: bold;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -55%);
}

</style>
